import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import JSZip from "jszip";
import { useGetResources } from "../../hooks/useGetResources";
import { getThemes, getFiles, getCompetencies, FileItem } from "./helper";
import NoDataScreen from "../../components/EmptyScreen/EmptyScreen";
import images from "../../assets/Icons/Icons";
import { trackEvent } from "../../mixpanel";
import { MIXPANEL_EVENTS } from "../../mixpanel/events";
import "./Resources.scss";

const THEMES_SHOW_LIMIT = 6;

const COMPLEXITY_LABELS: Record<string, string> = {
  easy: "Ages 4-5",
  medium: "Ages 6-7",
  hard: "Ages 8-9",
  difficult: "Ages 10-11",
};

const FIXED_COMPLEXITIES = ["easy", "medium", "hard", "difficult"];

const CheckIcon = () => (
  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
    <path
      d="M1 4L3.5 6.5L9 1"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Resources = () => {
  const navigate = useNavigate();
  const { data, loading } = useGetResources();

  const [pendingCompetencies, setPendingCompetencies] = useState<string[]>([]);
  const [appliedCompetencies, setAppliedCompetencies] = useState<string[]>([]);
  const [pendingThemes, setPendingThemes] = useState<string[]>([]);
  const [appliedThemes, setAppliedThemes] = useState<string[]>([]);
  const [themeSearch, setThemeSearch] = useState("");
  const [showAllThemes, setShowAllThemes] = useState(false);
  const [activeComplexity, setActiveComplexity] = useState<string | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);

  const togglePendingCompetency = (name: string) => {
    setPendingCompetencies((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name],
    );
  };

  const handleApplyCompetencyFilters = () => {
    setAppliedCompetencies(pendingCompetencies);
  };

  const handleCancelCompetencies = () => {
    setAppliedCompetencies([]);
    setPendingCompetencies([]);
  };

  const togglePendingTheme = (name: string) => {
    setPendingThemes((prev) =>
      prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name],
    );
  };

  const handleApplyThemeFilters = () => {
    setAppliedThemes(pendingThemes);
  };

  const handleCancelThemes = () => {
    setAppliedThemes([]);
    setPendingThemes([]);
  };

  const handleResetAll = () => {
    setPendingCompetencies([]);
    setAppliedCompetencies([]);
    setPendingThemes([]);
    setAppliedThemes([]);
    setThemeSearch("");
    setActiveComplexity(null);
  };

  const competenciesChanged =
    [...pendingCompetencies].sort().join(",") !==
    [...appliedCompetencies].sort().join(",");

  const themesChanged =
    [...pendingThemes].sort().join(",") !== [...appliedThemes].sort().join(",");

  const competencies = useMemo(() => getCompetencies(data), [data]);

  const availableThemes = useMemo(
    () => getThemes(data, appliedCompetencies, {}),
    [data, appliedCompetencies],
  );

  const filteredThemes = useMemo(
    () =>
      themeSearch
        ? availableThemes.filter((t) =>
            t.toLowerCase().includes(themeSearch.toLowerCase()),
          )
        : availableThemes,
    [availableThemes, themeSearch],
  );

  const visibleFiles = useMemo(
    () =>
      getFiles(data, {
        competencies:
          appliedCompetencies.length > 0 ? appliedCompetencies : undefined,
        complexity: activeComplexity ?? undefined,
        themes: appliedThemes.length > 0 ? appliedThemes : undefined,
      }),
    [data, appliedCompetencies, appliedThemes, activeComplexity],
  );

  const toggleFileSelection = (url: string) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      next.has(url) ? next.delete(url) : next.add(url);
      return next;
    });
  };

  const allSelected =
    visibleFiles.length > 0 &&
    visibleFiles.every((f) => selectedFiles.has(f.url));

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(visibleFiles.map((f) => f.url)));
    }
  };

  const handleDownload = async () => {
    const toDownload =
      selectedFiles.size > 0
        ? visibleFiles.filter((f) => selectedFiles.has(f.url))
        : visibleFiles;

    trackEvent(MIXPANEL_EVENTS.RESOURCE_DOWNLOADED, {
      competencies: appliedCompetencies,
      ageGroup: activeComplexity ? COMPLEXITY_LABELS[activeComplexity] : null,
      filesCount: toDownload.length,
    });

    const zip = new JSZip();
    await Promise.all(
      toDownload.map(async (f) => {
        const res = await fetch(f.url);
        const blob = await res.blob();
        zip.file(f.name, blob);
      }),
    );

    const content = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(content);
    const a = document.createElement("a");
    a.href = url;
    a.download = "worksheets.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="resources-page">
      <p className="resources-subtitle">
        Help students learn better by going an extra mile with these additional
        resources.
      </p>
      <hr className="resources-divider" />

      <div className="resources-body">
        <aside className="resources-sidebar">
          <div className="filter-section">
            <h3 className="filter-label">Filter by competency</h3>
            <div className="competency-list">
              {competencies.map((comp) => (
                <label
                  key={comp}
                  className="checkbox-row"
                  onClick={() => togglePendingCompetency(comp)}
                >
                  <div
                    className={`checkbox-box ${
                      pendingCompetencies.includes(comp) ? "checked" : ""
                    }`}
                  >
                    {pendingCompetencies.includes(comp) && <CheckIcon />}
                  </div>
                  <span className="checkbox-label">{comp}</span>
                </label>
              ))}
            </div>
            <div className="filter-actions">
              <button className="btn-cancel" onClick={handleCancelCompetencies}>
                Cancel
              </button>
              <button
                className="btn-apply"
                onClick={handleApplyCompetencyFilters}
                disabled={!competenciesChanged}
              >
                Apply filters
              </button>
            </div>
          </div>

          <div className="filter-section">
            <h3 className="filter-label">Filter by themes</h3>
            <div className="theme-search-wrap">
              <svg
                className="search-icon"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="11"
                  cy="11"
                  r="8"
                  stroke="#9ca3af"
                  strokeWidth="2"
                />
                <path
                  d="m21 21-4.35-4.35"
                  stroke="#9ca3af"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <input
                type="text"
                placeholder="Search"
                className="theme-search-input"
                value={themeSearch}
                onChange={(e) => setThemeSearch(e.target.value)}
                disabled={appliedCompetencies.length === 0}
              />
            </div>

            {appliedCompetencies.length === 0 ? (
              <div className="themes-placeholder">
                <div className="placeholder-lines">
                  <div className="placeholder-line" />
                  <div className="placeholder-line placeholder-line--short" />
                  <div className="placeholder-line" />
                </div>
                <p className="placeholder-text">
                  Themes will load after you choose competencies.
                </p>
              </div>
            ) : (
              <>
                <div
                  className={`themes-checkbox-list ${showAllThemes ? "themes-checkbox-list--expanded" : ""}`}
                >
                  {filteredThemes
                    .slice(0, showAllThemes ? undefined : THEMES_SHOW_LIMIT)
                    .map((theme) => (
                      <label
                        key={theme}
                        className="checkbox-row"
                        onClick={() => togglePendingTheme(theme)}
                      >
                        <div
                          className={`checkbox-box ${pendingThemes.includes(theme) ? "checked" : ""}`}
                        >
                          {pendingThemes.includes(theme) && <CheckIcon />}
                        </div>
                        <span className="checkbox-label">{theme}</span>
                      </label>
                    ))}
                </div>
                {filteredThemes.length > THEMES_SHOW_LIMIT && (
                  <button
                    className="btn-view-toggle"
                    onClick={() => setShowAllThemes((v) => !v)}
                  >
                    {showAllThemes ? "↑ View less" : "↓ View all"}
                  </button>
                )}
                <div className="filter-actions">
                  <button className="btn-cancel" onClick={handleCancelThemes}>
                    Cancel
                  </button>
                  <button
                    className="btn-apply"
                    onClick={handleApplyThemeFilters}
                    disabled={!themesChanged}
                  >
                    Apply filters
                  </button>
                </div>
              </>
            )}
          </div>

          {(appliedCompetencies.length > 0 || appliedThemes.length > 0) && (
            <button className="btn-reset-all" onClick={handleResetAll}>
              Reset all
            </button>
          )}
        </aside>

        <main className="resources-main">
          {loading && (
            <div className="resources-loading">
              <div className="skeleton-section">
                <div className="skeleton skeleton--title" />
                <div className="skeleton-grid">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="skeleton skeleton--card" />
                  ))}
                </div>
              </div>
            </div>
          )}

          {!loading && (
            <>
              <div className="worksheets-header">
                {isSelecting ? (
                  <>
                    <button
                      className="btn-select-all"
                      onClick={handleSelectAll}
                    >
                      <span
                        className={`select-all-circle ${allSelected ? "select-all-circle--filled" : ""}`}
                      >
                        {allSelected && <CheckIcon />}
                      </span>
                      Select All
                    </button>
                    <div className="worksheets-actions">
                      <button
                        className="btn-cancel-pdf"
                        onClick={() => {
                          setIsSelecting(false);
                          setSelectedFiles(new Set());
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-download-active"
                        onClick={handleDownload}
                      >
                        <img src={images.pdfDownload} width={14} height={14} />
                        Download PDF
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="worksheets-title">
                      <img
                        src={images.worksheet}
                        alt=""
                        className="worksheets-icon"
                      />
                      <span>Worksheets</span>
                    </div>
                    <div className="worksheets-actions">
                      <button
                        className="btn-select"
                        onClick={() => setIsSelecting(true)}
                      >
                        Select
                      </button>
                      <button className="btn-download" disabled>
                        <img src={images.pdfDownload} width={14} height={14} />
                        Download PDF
                      </button>
                    </div>
                  </>
                )}
              </div>

              <div className="complexity-tabs">
                {FIXED_COMPLEXITIES.map((c) => (
                  <button
                    key={c}
                    className={`complexity-tab ${activeComplexity === c ? "complexity-tab--active" : ""}`}
                    onClick={() =>
                      setActiveComplexity(activeComplexity === c ? null : c)
                    }
                  >
                    {COMPLEXITY_LABELS[c]}
                  </button>
                ))}
              </div>

              {visibleFiles.length === 0 ? (
                <NoDataScreen message="No worksheets found" />
              ) : (
                <div className="files-grid">
                  {visibleFiles.map((file: FileItem, idx: number) => {
                    const isSelected = selectedFiles.has(file.url);
                    return (
                      <div
                        key={idx}
                        className={`file-card ${isSelected ? "file-card--selected" : ""} ${
                          isSelecting ? "file-card--selectable" : ""
                        }`}
                        onClick={() =>
                          isSelecting
                            ? toggleFileSelection(file.url)
                            : setPreviewFile(file)
                        }
                      >
                        <img
                          src={file.imageurl}
                          alt={file.name}
                          className="file-thumbnail"
                        />
                        {isSelecting && (
                          <>
                            <div className="file-card-overlay" />
                            <div
                              className={`card-circle ${isSelected ? "card-circle--checked" : ""}`}
                            >
                              {isSelected && <CheckIcon />}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {previewFile && (
        <div className="preview-overlay" onClick={() => setPreviewFile(null)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="btn-preview-close"
              onClick={() => setPreviewFile(null)}
            >
              <img src={images.closeIcon} width={42} height={42} />
            </button>
            <div className="preview-image-wrap">
              <button
                className="btn-preview-download-floating"
                onClick={async () => {
                  const res = await fetch(previewFile.url);
                  const blob = await res.blob();
                  const blobUrl = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = blobUrl;
                  a.download = previewFile.name;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(blobUrl);
                }}
              >
                <img src={images.pdfDownload} width={24} height={32} />
              </button>
              <img src={previewFile.imageurl} alt={previewFile.name} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Resources;
